import { api } from "../../api"
import { setAlertFunc } from "../../components/AlertToaster/AlertSlice"
import {
    HistoryResponse,
    IResponse,
    StatusButton,
    TabsResponseType,
    TicketDataResponseType,
} from "../Common/commonTypes"
import { getProjectsData, getProjectsTabs, getTicketDetails } from "../Projects/ProjectSlice"
import { getTicketDetails as getDemolitionTicketDetails } from "../Operations/features/Demolition/DemolitionSlice"

const MODULE_URL = `finance`
interface GetSkipStatusesParams {
    search_key?: string
    ticket_id: string
}

interface IParsedFields {
    amount_without_gst: number
    gst_amount: number
    gst_no: string
    credit_note_no: string
    credit_note_date: string
    invoice_date: string
    invoice_no: string
    irn_no: string
    purchase_no: string
    shipping_charges: number
    subtotal: number
    total_amount: number
    revenue_tagging_note: string
}
interface IExtractTaxInvoiceRes {
    low_confidence_flags: {
        amount_without_gst: boolean
        shipping_charges: boolean
    }
    message: string
    parsed_fields: IParsedFields
    s3_url: string
}

const financeService = api.injectEndpoints({
    endpoints: build => ({
        getFinanceTabs: build.query({
            query: params => ({
                url: `${MODULE_URL}/ticket_total`,
                params: {
                    state_id: JSON.stringify(params.data.stateId ?? []),
                    status_id: JSON.stringify(params.data.statusId ?? []),
                    city_id: JSON.stringify(params.data.cityId ?? []),
                    assigned_to: params.data.assigned_to?.id ?? "",
                    age: JSON.stringify(params.data.age ?? []),
                    tag_ids: JSON.stringify(params.data.tags ?? []),
                    age_op: params.data.age_op ?? "",
                    deal_type: params.data.dealType ?? "",
                    raised_on_end: params.data.TicketEndDate ?? "",
                    raised_on_start: params.data.TicketStartDate ?? "",
                    due_start: params.data.ClosureStartDate ?? "",
                    due_end: params.data.ClosureEndDate ?? "",
                    charger_type: JSON.stringify(params.data.chargerIds ?? []),
                    search_key: params.data.search_key ?? "",
                    read_filter: params.data.ReadUnread ?? "all",
                    per_page: params?.selectedValue,
                    page: params?.page,
                    site_ids: JSON.stringify(params.data.siteIds ?? []),
                    account_id: JSON.stringify(params.data.account_id ?? []),
                    parent_account_id: JSON.stringify(params.data.parent_account_id ?? []),
                    ecom_order_id: params.data.ecom_order_id ? JSON.stringify([params.data.ecom_order_id]) : JSON.stringify([]),
                },
            }),
            transformResponse: (response: TabsResponseType) => response.data.message,
            providesTags: ["Finance"],
        }),
        getFinanceData: build.query<TicketDataResponseType, { data: any; selectedValue: number; page: number }>({
            query: params => ({
                url: `${MODULE_URL}/${params.data.parent_status ?? "all_tab"}`,
                params: {
                    state_id: JSON.stringify(params.data.stateId ?? []),
                    status_id: JSON.stringify(params.data.statusId ?? []),
                    city_id: JSON.stringify(params.data.cityId ?? []),
                    assigned_to: params.data.assigned_to?.id ?? "",
                    tag_ids: JSON.stringify(params.data.tags ?? []),
                    age: JSON.stringify(params.data.age ?? []),
                    age_op: params.data.age_op ?? "",
                    deal_type: params.data.dealType ?? "",
                    raised_on_end: params.data.TicketEndDate ?? "",
                    raised_on_start: params.data.TicketStartDate ?? "",
                    due_start: params.data.ClosureStartDate ?? "",
                    due_end: params.data.ClosureEndDate ?? "",
                    charger_type: JSON.stringify(params.data.chargerIds ?? []),
                    search_key: params.data.search_key ?? "",
                    read_filter: params.data.ReadUnread ?? "all",
                    per_page: params?.selectedValue,
                    page: params?.page,
                    site_ids: JSON.stringify(params.data.siteIds ?? []),
                    account_id: JSON.stringify(params.data.account_id ?? []),
                    parent_account_id: JSON.stringify(params.data.parent_account_id ?? []),
                    ecom_order_id: params.data.ecom_order_id ? JSON.stringify([params.data.ecom_order_id]) : JSON.stringify([]),
                },
            }),
            providesTags: ["Finance"],
        }),
        getPODDetails: build.query<any, { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/get_pd_details`,
                params: {
                    ticket_id,
                },
            }),
        }),
        getTicketDetails: build.query<IResponse<any>, { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/ticket_details`,
                params: {
                    ticket_id,
                },
            }),
            providesTags: (result, error, arg) => [{ type: "Finance", id: arg.ticket_id }],
        }),
        getOtherDetails: build.query<IResponse<any>, { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/other_details`,
                params: {
                    ticket_id,
                },
            }),
            providesTags: (result, error, arg) => [{ type: "Finance", id: arg.ticket_id }],
        }),
        getPaymentTermsData: build.query<any, { ticket_id: string; ticket_type?: string }>({
            query: params => ({
                url: `${MODULE_URL}/get_payment_terms`,
                params: {
                    ticket_id: params.ticket_id,
                    ticket_type: params?.ticket_type || "",
                },
            }),
        }),
        getChargerFlag: build.query<any, { ticket_id: string; project_id: string }>({
            query: params => ({
                url: `${MODULE_URL}/approval_charger_modal`,
                params: {
                    ticket_id: params.ticket_id,
                    project_id: params?.project_id || "",
                },
            }),
        }),
        getSkipStatuses: build.query<IResponse<any>, GetSkipStatusesParams>({
            query: ({ search_key, ticket_id }) => ({
                url: `${MODULE_URL}/statuses`,
                params: {
                    search_key: search_key ?? "",
                },
            }),
            providesTags: (result, error, arg) => [{ type: "Finance", id: arg?.ticket_id }],
        }),
        getTicketHistory: build.query<HistoryResponse, { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/ticket_history`,
                params: {
                    ticket_id,
                },
            }),
            providesTags: (result, error, arg) => [{ type: "Finance", id: arg.ticket_id }],
        }),
        getPaymentConditions: build.query<IResponse<{ id: number; name: string }[]>, { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/conditions`,
                params: {
                    ticket_id,
                },
            }),
            providesTags: (result, error, arg) => [{ type: "Finance", id: arg.ticket_id }],
        }),
        getPaymentTime: build.query<HistoryResponse, { ticket_id: string }>({
            query: () => ({
                url: `${MODULE_URL}/time_uom`,
            }),
            providesTags: (result, error, arg) => [{ type: "Finance", id: arg.ticket_id }],
        }),
        // for getting button dropdown options
        getFinanceButtonDropdown: build.query({
            query: (params: { ticket_id: string }) => ({
                url: `${MODULE_URL}/button`,
                params: params,
            }),
            transformResponse: (response: IResponse<StatusButton[]>) => response.data,
            providesTags: (result, error, arg) => [{ type: "Finance", id: arg.ticket_id }],
        }),
        getChargerTemplate: build.mutation<any, { ticket_id: string; charger_details: any }>({
            query: body => ({
                url: `${MODULE_URL}/charger_operation`,
                method: "POST",
                body: body,
                responseHandler: (response: Response) => response.blob(),
                cache: "no-cache",
            }),
            async onQueryStarted(arg, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    const url = window.URL.createObjectURL(data)
                    const a = document.createElement("a")
                    a.href = url
                    a.download = `charger_template_${arg.ticket_id}.xlsx`
                    document.body.appendChild(a)
                    a.click()
                    window.URL.revokeObjectURL(url)
                    document.body.removeChild(a)
                } catch (error) {
                    console.error("Download failed:", error)
                }
            },
        }),
        getTaxInvoices: build.query<any, { id?: string; ticket_id?: string }>({
            query: params => ({
                url: `${MODULE_URL}/tax_invoice`,
                params,
            }),
            providesTags: (_, __, arg) => [{ type: "Finance", id: arg.ticket_id }, "Finance"],
        }),
        uploadBulkCharger: build.mutation<{ message: string }, { ticket_id: string; payload: FormData }>({
            query: body => ({
                url: `${MODULE_URL}/charger_operation`,
                method: "PUT",
                body: body.payload,
            }),
            invalidatesTags: (_, __, arg) => [{ type: "Finance", id: Number(arg.ticket_id) }],
        }),
        // for changing ticket status
        editFinanceTicketStatus: build.mutation<IResponse<unknown>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/ticket`,
                method: "PUT",
                body: body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.data }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Finance", id: arg.get("ticket_id") as string },
                "Finance",
                "Logistics",
                "Installation",
            ],
        }),
        editSkipStatus: build.mutation<IResponse<unknown>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/skip_to_status`,
                method: "PUT",
                body: body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.data }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Finance", id: arg.get("ticket_id") as string },
                "Finance",
            ],
        }),
        editTaxInvoice: build.mutation<{ msg: string }, FormData>({
            query: body => ({
                url: `${MODULE_URL}/tax_invoice`,
                method: "PUT",
                body: body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.msg }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Finance", id: arg.get("ticket_id") as string },
                "Finance",
            ],
        }),
        addTicketDetails: build.mutation<IResponse<unknown>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/ticket_details`,
                method: "POST",
                body: body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.data }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Finance", id: arg.get("ticket_id") as string },
                "Finance",
            ],
        }),
        addPaymentTerm: build.mutation<IResponse<unknown>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/payment_term`,
                method: "POST",
                body: body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.data }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Finance", id: arg.get("ticket_id") as string },
                "Finance",
                "Projects",
            ],
        }),
        addTransaction: build.mutation<{ msg: string }, { data: FormData; module?: string }>({
            query: body => ({
                url: `${MODULE_URL}/transaction`,
                method: "POST",
                body: body.data,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    const urlParams = new URLSearchParams(window.location.search)
                    const ticketId = urlParams.get("ticket_id")
                    if (arg?.module === "demolitions") {
                        dispatch(getDemolitionTicketDetails(ticketId))
                    } else {
                        dispatch(getTicketDetails(ticketId))
                    }
                    dispatch(setAlertFunc({ type: "success", msg: data?.msg }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message || "Something went wrong" }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Finance", id: arg.data.get("ticket_id") as string },
                "Finance",
                "Projects",
            ],
        }),
        editTransaction: build.mutation<
            { msg: string },
            { data: FormData; view: "table" | "details"; module?: string }
        >({
            query: body => ({
                url: `${MODULE_URL}/transaction`,
                method: "PUT",
                body: body.data,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
                try {
                    const { data } = await queryFulfilled
                    const state: any = getState()
                    const { ProjectsFormValue, page, selectedValue } = state.Projects
                    dispatch(setAlertFunc({ type: "success", msg: data?.msg }))
                    // To be removed after projects RTK integration
                    if (arg.view === "table") {
                        dispatch(getProjectsTabs({ data: ProjectsFormValue, page: page, selectedValue: selectedValue }))
                        dispatch(getProjectsData({ data: ProjectsFormValue, page: page, selectedValue: selectedValue }))
                    } else {
                        const urlParams = new URLSearchParams(window.location.search)
                        const ticketId = urlParams.get("ticket_id")
                        if (arg?.module === "demolitions") {
                            dispatch(getDemolitionTicketDetails(ticketId))
                        } else {
                            dispatch(getTicketDetails(ticketId))
                        }
                    }
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message || "Something went wrong" }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Finance", id: arg.data.get("ticket_id") as string },
                "Finance",
                "Projects",
            ],
        }),

        addTaxInvoice: build.mutation<{ msg: string }, { data: FormData; invalidateCache?: boolean }>({
            query: body => ({
                url: `${MODULE_URL}/tax_invoice`,
                method: "POST",
                body: body.data,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.msg }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) =>
                arg.invalidateCache === false
                    ? []
                    : [{ type: "Finance", id: arg.data.get("ticket_id") as string }, "Finance"],
        }),
        VerifyTransaction: build.mutation<IResponse<unknown>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/verify_transaction`,
                method: "POST",
                body: body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.data }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Finance", id: arg.get("ticket_id") as string },
                "Finance",
            ],
        }),
        deleteTransaction: build.mutation<IResponse<any>, { data: any }>({
            query: ({ data }) => ({
                url: `${MODULE_URL}/transaction?ticket_id=${data?.ticket_id}&user_id=${data?.user_id}&transaction_id=${data?.transaction_id}`,
                method: "DELETE",
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.data }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Finance", id: arg?.data?.ticket_id as string },
                "Finance",
            ],
        }),
        deletePaymentTerm: build.mutation<IResponse<any>, { data: any }>({
            query: ({ data }) => ({
                url: `${MODULE_URL}/payment_term?ticket_id=${data?.ticket_id}&user_id=${data?.user_id}&payment_term_id=${data?.payment_term_id}`,
                method: "DELETE",
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.data }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Finance", id: arg?.data?.ticket_id as string },
                "Finance",
            ],
        }),
        deleteTaxInvoice: build.mutation<{ msg: string }, { data: any }>({
            query: ({ data }) => ({
                url: `${MODULE_URL}/tax_invoice?ticket_id=${data?.ticket_id}&user_id=${data?.user_id}&id=${data?.id}`,
                method: "DELETE",
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.msg }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Finance", id: arg?.data?.ticket_id as string },
                "Finance",
            ],
        }),
        addPaymentTermsInBulk: build.mutation<IResponse<any>, FormData>({
            query: data => ({
                url: `${MODULE_URL}/add_payment_terms_in_bulk`,
                method: "POST",
                body: data,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.data }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (_, __, arg) => [{ type: "Finance", id: arg?.get("ticket_id") as string }, "Finance"],
        }),
        extractTaxInvoice: build.mutation<IExtractTaxInvoiceRes, FormData>({
            query: data => ({
                url: `${MODULE_URL}/extract-tax-invoice`,
                method: "POST",
                body: data,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.error?.data?.error }))
                }
            },
        }),
        saveTaxInvoiceData: build.mutation<{ msg: string }, FormData>({
            query: data => ({
                url: `${MODULE_URL}/tax-invoice`,
                method: "POST",
                body: data,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.error?.data?.error }))
                }
            },
        }),
    }),
})
export const {
    useLazyGetFinanceTabsQuery,
    useLazyGetFinanceDataQuery,
    useGetTicketDetailsQuery,
    useGetTicketHistoryQuery,
    useGetFinanceButtonDropdownQuery,
    useLazyGetPaymentTermsDataQuery,
    useLazyGetPODDetailsQuery,
    useGetSkipStatusesQuery,
    useGetChargerFlagQuery,
    useGetOtherDetailsQuery,
    useGetPaymentConditionsQuery,
    useGetPaymentTimeQuery,
    useGetTaxInvoicesQuery,
    useGetChargerTemplateMutation,
    useEditFinanceTicketStatusMutation,
    useEditSkipStatusMutation,
    useEditTaxInvoiceMutation,
    useVerifyTransactionMutation,
    useAddPaymentTermMutation,
    useAddTaxInvoiceMutation,
    useAddTicketDetailsMutation,
    useEditTransactionMutation,
    useAddTransactionMutation,
    useDeletePaymentTermMutation,
    useDeleteTaxInvoiceMutation,
    useDeleteTransactionMutation,
    useUploadBulkChargerMutation,
    useAddPaymentTermsInBulkMutation,
    useExtractTaxInvoiceMutation,
    useSaveTaxInvoiceDataMutation,
} = financeService

export { financeService }
